import m, {MithrilComponent} from '@bitstillery/mithril'
import {Dialog, type DialogAttrs} from '@bitstillery/common/components/portal'
import {classes} from '@bitstillery/common/lib/utils'

/**
 * The catalog's `Dialog` — the popup as the portal wears it, carrying the two things authored JSX
 * cannot.
 *
 * The frosted portal skin is a class (`dialog-portal`, see `scss/elements/_dialog.scss`) and a CMS view
 * writes no classes, so the wrapper puts it on — along with `c-cms-popup`, which is what the co-located
 * SCSS hangs the on-glass ink off.
 *
 * And *acting* on the popup dismisses it, the rule `PopupOffer` applies to its own CTA: the call to
 * action leads to a CMS page, which the same content frame renders, so a popup that only closed on ×
 * would greet the visitor again the moment they followed it.
 */
export class CmsPopupDialog extends MithrilComponent<DialogAttrs> {
    /** Read by the listener. Held as a field rather than closed over: Mithril allocates a fresh vnode per
     *  redraw, so a closure over `oncreate`'s would pin that first render's whole subtree — and its
     *  `onclose` — for as long as the popup is mounted. */
    onclose?: (() => unknown) | undefined

    dismiss_on_action = (event: Event) => {
        if (event.target instanceof Element && event.target.closest('a, button')) {
            this.onclose?.()
        }
    }

    override oncreate(vnode: m.VnodeDOM<DialogAttrs>) {
        // Capture phase: a `Button` carrying a `link` calls `stopPropagation()` before it navigates, so
        // a bubbling listener would never see the one click that matters most. `Dropdown` closes on
        // select in capture for the same reason.
        vnode.dom.addEventListener('click', this.dismiss_on_action, true)
    }

    override onremove(vnode: m.VnodeDOM<DialogAttrs>) {
        vnode.dom.removeEventListener('click', this.dismiss_on_action, true)
    }

    view(vnode: m.Vnode<DialogAttrs>) {
        this.onclose = vnode.attrs.onclose
        return (
            <Dialog {...vnode.attrs} className={classes('dialog-portal', 'c-cms-popup', vnode.attrs.className)}>
                {vnode.children}
            </Dialog>
        )
    }
}
